/**
 * What to tell the reader when a request fails, in their language (uiv2 7e).
 *
 * The API answers an error as `AppError`'s `{ error: { message, code,
 * params? } }`; the CODE is worded from `errors.code.*`, and the English
 * `message` is for logs and API callers. `api-client` re-exports everything
 * here. Kept apart so Stryker can mutate it without the interceptors.
 */
import axios from 'axios';
// The i18next singleton `@/i18n` initialises — not `@/i18n` itself, which would pull
// react-i18next into every module (and test) that reads an error.
import i18n from 'i18next';

/** i18next's words, or the key itself before `@/i18n` has initialised it (an identity-`t` test). */
function say(key: string, options?: Record<string, unknown>): string {
  return i18n.isInitialized ? i18n.t(key, options) : key;
}

interface ErrorBody {
  message?: string;
  code?: string;
  params?: Record<string, string | number>;
}

/**
 * The server answers an error as `AppError`'s `{ error: { message, code,
 * params? } }` (every route since uiv2 7e). Two older shapes are still read —
 * a bare `{ error: "…" }` and a top-level `{ message, code }` — for a server
 * one release behind during a deploy. Reading only the last one showed users
 * axios's "Request failed with status code 400" in place of the server's
 * sentence.
 */
function errorBody(data: unknown): ErrorBody {
  if (typeof data !== 'object' || data === null) return {};
  const body = data as { error?: unknown; message?: unknown; code?: unknown };
  // A property read on a string `error` is undefined, so no shape check is needed.
  const nested = (body.error ?? {}) as { message?: unknown; code?: unknown; params?: ErrorBody['params'] };
  // An empty string is kept: `wordBody` treats it as no message at all.
  const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
  return {
    message: str(nested.message) ?? str(body.error) ?? str(body.message),
    code: str(nested.code) ?? str(body.code),
    params: nested.params,
  };
}

/**
 * A server error that did not arrive as an axios rejection — a `success:
 * false` envelope, or a body read with `fetch` — carried so `getErrorMessage`
 * words it like any other.
 */
export class ServerError extends Error {
  readonly body: ErrorBody;
  constructor(
    body: unknown,
    readonly status?: number
  ) {
    const parsed = errorBody({ error: body });
    super(parsed.message ?? 'Server error');
    this.name = 'ServerError';
    this.body = parsed;
  }
}

/** An error whose message is already in the reader's language — shown as is. */
export class ShownError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ShownError';
  }
}

/** The server's machine-readable error code, when it sent one. */
export function getErrorCode(error: unknown): string | undefined {
  if (axios.isAxiosError(error)) return errorBody(error.response?.data).code;
  if (error instanceof ServerError) return error.body.code;
  return undefined;
}

/** Words for a response that carried no code of ours — nginx's own pages, a dropped connection. */
function statusText(status: number | undefined): string {
  if (status === undefined) return say('errors.network');
  if (status === 403) return say('errors.forbidden');
  if (status === 404) return say('errors.notFound');
  if (status === 413) return say('errors.fileTooLarge');
  if (status === 429) return say('errors.tooManyRequests');
  if (status >= 500) return say('errors.serverError');
  return say('errors.generic');
}

/**
 * An API error code in the reader's language (`errors.code.*`), or `null` for
 * a code this client has no words for. Also for codes that arrive outside an
 * error response — a per-file upload result's `errorCode`.
 */
export function wordErrorCode(code: string | undefined, params?: Record<string, string | number>): string | null {
  if (!code) return null;
  const key = `errors.code.${code}`;
  return i18n.isInitialized && i18n.exists(key) ? say(key, params ?? {}) : null;
}

function wordBody(body: ErrorBody, status: number | undefined, fallback: string | undefined): string {
  const worded = wordErrorCode(body.code, body.params);
  if (worded !== null) return worded;
  // A code this client has no words for (a newer server), or no code: the server's own text.
  if (body.message) return body.message;
  // No response at all is the connection, whatever the caller was doing.
  if (status === undefined) return statusText(undefined);
  return fallback ?? statusText(status);
}

/**
 * What to tell the reader, in their language (uiv2 7e): the server's code
 * worded from `errors.code.*` with its `params`; else the server's text; else
 * the caller's `fallback` (a sentence naming what failed); else words for the
 * status. An `Error` the client threw itself is developer text ("No data
 * returned from API") — the reader sees the fallback or `errors.generic`.
 */
export function getErrorMessage(error: unknown, fallback?: string): string {
  if (axios.isAxiosError(error)) return wordBody(errorBody(error.response?.data), error.response?.status, fallback);
  if (error instanceof ServerError) return wordBody(error.body, error.status ?? 200, fallback);
  if (error instanceof ShownError) return error.message;
  return fallback ?? say('errors.generic');
}
